import { useState } from "react";
import { View, Flex, Text } from "@aws-amplify/ui-react";
import { SkillRatingBar } from "./SkillRatingBar";
import type { RatingDisplayStyle } from "../hooks/useRatingDisplayPreference";

interface RatingDisplayProps {
  rating: number;
  maxRating?: number;
  style: RatingDisplayStyle;
  size?: "small" | "medium" | "large";
  interactive?: boolean;
  onRatingChange?: (newRating: number) => void;
  skillName?: string;
}

const ratingLabels = [
  "No capability",
  "Basic proficiency",
  "Some experience",
  "Average expertise",
  "Advanced",
  "Expert",
];

export function RatingDisplay({
  rating,
  maxRating = 5,
  style,
  size = "medium",
  interactive = false,
  onRatingChange,
  skillName = "Skill",
}: RatingDisplayProps) {
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);

  // Helper function to get label based on maxRating
  const getLabel = (value: number): string => {
    if (maxRating === 10) {
      // For percentage display (0-10 scale), show as percentage
      return `${value * 10}%`;
    }
    // For regular 1-5 scale, use rating labels
    return `${value}: ${ratingLabels[value]}`;
  };
  const sizes = {
    small: {
      bar: { width: "80px", height: "6px" },
      icon: "0.9rem",
      iconSize: "10px",
    },
    medium: {
      bar: { width: "100px", height: "8px" },
      icon: "1.2rem",
      iconSize: "12px",
    },
    large: {
      bar: { width: "120px", height: "10px" },
      icon: "1.5rem",
      iconSize: "14px",
    },
  };

  const currentSize = sizes[size];

  // Slider style - use the new SkillRatingBar component
  if (style === "slider") {
    return (
      <SkillRatingBar
        skillName={skillName}
        rating={rating}
        maxRating={maxRating}
        interactive={interactive}
        onRatingChange={onRatingChange}
        showPercentage={true}
      />
    );
  }

  if (style === "bar") {
    return (
      <View style={{ width: "100%" }}>
        {/* Skill name header */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          marginBottom="0.5rem"
        >
          <Text fontSize="0.9375rem" fontWeight="500" color="#111827">
            {skillName}
          </Text>
          <Text fontSize="0.875rem" color="#6b7280">
            {Math.round((rating / maxRating) * 100)}%
          </Text>
        </Flex>

        <View
          style={{
            position: "relative",
            display: "inline-block",
            width: "100%",
          }}
        >
          <View
            width="100%"
            height={currentSize.bar.height}
            backgroundColor="var(--amplify-colors-neutral-20)"
            borderRadius="4px"
            style={{
              overflow: "hidden",
              cursor: interactive ? "pointer" : "default",
              position: "relative",
            }}
            onClick={(e) => {
              if (interactive && onRatingChange) {
                const rect = (e.target as HTMLElement).getBoundingClientRect();
                const x = e.clientX - rect.left;
                const percentage = x / rect.width;
                const newRating = Math.max(
                  0,
                  Math.min(maxRating, Math.ceil(percentage * maxRating)),
                );
                onRatingChange(newRating);
              }
            }}
            onMouseMove={(e) => {
              const rect = (e.target as HTMLElement).getBoundingClientRect();
              const x = e.clientX - rect.left;
              const percentage = x / rect.width;
              const hoverRating = Math.max(
                0,
                Math.min(maxRating, Math.ceil(percentage * maxRating)),
              );
              setHoveredRating(interactive ? hoverRating : rating);
            }}
            onMouseEnter={() => !interactive && setHoveredRating(rating)}
            onMouseLeave={() => setHoveredRating(null)}
          >
            <View
              height="100%"
              backgroundColor={
                (() => {
                  const pct = (rating / maxRating) * 100;
                  if (pct >= 80) return '#22c55e'; // Green - Expert (4-5)
                  if (pct >= 60) return '#3b82f6'; // Blue - Good (3-4)
                  if (pct >= 40) return '#eab308'; // Yellow - Medium (2-3)
                  if (pct >= 20) return '#f97316'; // Orange - Low (1-2)
                  return '#ef4444'; // Red - Very low (0-1)
                })()
              }
              width={`${(rating / maxRating) * 100}%`}
              style={{ transition: "width 0.3s ease, background-color 0.3s ease", pointerEvents: "none" }}
            />
          </View>
          {hoveredRating !== null && (
            <View
              style={{
                position: "absolute",
                bottom: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                marginBottom: "8px",
                backgroundColor: "#1f2937",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                zIndex: 1000,
                pointerEvents: "none",
              }}
            >
              {getLabel(hoveredRating)}
            </View>
          )}
        </View>
      </View>
    );
  }

  if (style === "stars") {
    return (
      <View style={{ width: "100%" }}>
        {/* Skill name header */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          marginBottom="0.5rem"
        >
          <Text fontSize="0.9375rem" fontWeight="500" color="#111827">
            {skillName}
          </Text>
          <Text fontSize="0.875rem" color="#6b7280">
            {Math.round(rating * 10) / 10}/{maxRating}
          </Text>
        </Flex>

        <View style={{ position: "relative", display: "inline-block" }}>
          <Flex
            gap="2px"
            onMouseEnter={() => !interactive && setHoveredRating(rating)}
            onMouseLeave={() => !interactive && setHoveredRating(null)}
          >
            {Array.from({ length: maxRating }).map((_, i) => (
              <Text
                key={i}
                fontSize={currentSize.icon}
                color="var(--amplify-colors-orange-60)"
                style={{ cursor: interactive ? "pointer" : "default" }}
                onClick={() => interactive && onRatingChange?.(i + 1)}
                onMouseEnter={() => interactive && setHoveredRating(i + 1)}
                onMouseLeave={() => interactive && setHoveredRating(null)}
              >
                {i < rating ? "★" : "☆"}
              </Text>
            ))}
          </Flex>
          {hoveredRating !== null && (
            <View
              style={{
                position: "absolute",
                bottom: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                marginBottom: "8px",
                backgroundColor: "#1f2937",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                zIndex: 1000,
                pointerEvents: "none",
              }}
            >
              {getLabel(hoveredRating)}
            </View>
          )}
        </View>
      </View>
    );
  }

  if (style === "dots") {
    return (
      <View style={{ width: "100%" }}>
        {/* Skill name header */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          marginBottom="0.5rem"
        >
          <Text fontSize="0.9375rem" fontWeight="500" color="#111827">
            {skillName}
          </Text>
          <Text fontSize="0.875rem" color="#6b7280">
            {Math.round(rating * 10) / 10}/{maxRating}
          </Text>
        </Flex>

        <View style={{ position: "relative", display: "inline-block" }}>
          <Flex
            gap="0.5rem"
            onMouseEnter={() => !interactive && setHoveredRating(rating)}
            onMouseLeave={() => !interactive && setHoveredRating(null)}
          >
            {Array.from({ length: maxRating }).map((_, i) => (
              <View
                key={i}
                width={currentSize.iconSize}
                height={currentSize.iconSize}
                borderRadius="50%"
                backgroundColor={
                  i < rating
                    ? "var(--amplify-colors-blue-60)"
                    : "var(--amplify-colors-neutral-20)"
                }
                style={{
                  transition: "background-color 0.3s ease",
                  cursor: interactive ? "pointer" : "default",
                }}
                onClick={() => interactive && onRatingChange?.(i + 1)}
                onMouseEnter={() => interactive && setHoveredRating(i + 1)}
                onMouseLeave={() => interactive && setHoveredRating(null)}
              />
            ))}
          </Flex>
          {hoveredRating !== null && (
            <View
              style={{
                position: "absolute",
                bottom: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                marginBottom: "8px",
                backgroundColor: "#1f2937",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                zIndex: 1000,
                pointerEvents: "none",
              }}
            >
              {getLabel(hoveredRating)}
            </View>
          )}
        </View>
      </View>
    );
  }

  if (style === "squares") {
    return (
      <View style={{ width: "100%" }}>
        {/* Skill name header */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          marginBottom="0.5rem"
        >
          <Text fontSize="0.9375rem" fontWeight="500" color="#111827">
            {skillName}
          </Text>
          <Text fontSize="0.875rem" color="#6b7280">
            {Math.round(rating * 10) / 10}/{maxRating}
          </Text>
        </Flex>

        <View style={{ position: "relative", display: "inline-block" }}>
          <Flex
            gap="0.5rem"
            onMouseEnter={() => !interactive && setHoveredRating(rating)}
            onMouseLeave={() => !interactive && setHoveredRating(null)}
          >
            {Array.from({ length: maxRating }).map((_, i) => (
              <View
                key={i}
                width={currentSize.iconSize}
                height={currentSize.iconSize}
                backgroundColor={
                  i < rating
                    ? "var(--amplify-colors-blue-60)"
                    : "var(--amplify-colors-neutral-20)"
                }
                style={{
                  transition: "background-color 0.3s ease",
                  cursor: interactive ? "pointer" : "default",
                }}
                onClick={() => interactive && onRatingChange?.(i + 1)}
                onMouseEnter={() => interactive && setHoveredRating(i + 1)}
                onMouseLeave={() => interactive && setHoveredRating(null)}
              />
            ))}
          </Flex>
          {hoveredRating !== null && (
            <View
              style={{
                position: "absolute",
                bottom: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                marginBottom: "8px",
                backgroundColor: "#1f2937",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                zIndex: 1000,
                pointerEvents: "none",
              }}
            >
              {getLabel(hoveredRating)}
            </View>
          )}
        </View>
      </View>
    );
  }

  if (style === "number") {
    const ratingColors = [
      "#db2828", // 0: red
      "#f2711c", // 1: orange
      "#fbbd08", // 2: yellow
      "#767676", // 3: gray
      "#b5cc18", // 4: lime
      "#21ba45", // 5: green
    ];

    if (interactive) {
      return (
        <View style={{ width: "100%" }}>
          {/* Skill name header */}
          <Flex
            justifyContent="space-between"
            alignItems="center"
            marginBottom="0.5rem"
          >
            <Text fontSize="0.9375rem" fontWeight="500" color="#111827">
              {skillName}
            </Text>
          </Flex>

          <View style={{ position: "relative", display: "inline-block" }}>
            <Flex gap="0.5rem">
              {Array.from({ length: maxRating }).map((_, i) => {
                const num = i + 1;
                const isSelected = num === rating;
                return (
                  <View
                    key={i}
                    width="32px"
                    height="32px"
                    backgroundColor={ratingColors[num]}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: isSelected ? "3px solid #000" : "1px solid #ccc",
                      borderRadius: "4px",
                      transition: "border 0.2s ease",
                    }}
                    onClick={() => onRatingChange?.(num)}
                    onMouseEnter={() => setHoveredRating(num)}
                    onMouseLeave={() => setHoveredRating(null)}
                  >
                    <Text fontWeight="bold" color="white" fontSize="1rem">
                      {num}
                    </Text>
                  </View>
                );
              })}
            </Flex>
            {hoveredRating !== null && (
              <View
                style={{
                  position: "absolute",
                  bottom: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  marginBottom: "8px",
                  backgroundColor: "#1f2937",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                  zIndex: 1000,
                  pointerEvents: "none",
                }}
              >
                {getLabel(hoveredRating)}
              </View>
            )}
          </View>
        </View>
      );
    }

    // Non-interactive: show skill name and number
    return (
      <View style={{ width: "100%" }}>
        {/* Skill name header */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          marginBottom="0.5rem"
        >
          <Text fontSize="0.9375rem" fontWeight="500" color="#111827">
            {skillName}
          </Text>
        </Flex>

        <View style={{ position: "relative", display: "inline-block" }}>
          <View
            width="32px"
            height="32px"
            backgroundColor={ratingColors[rating]}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
            }}
            onMouseEnter={() => setHoveredRating(rating)}
            onMouseLeave={() => setHoveredRating(null)}
          >
            <Text fontWeight="bold" color="white" fontSize="1rem">
              {rating}
            </Text>
          </View>
          {hoveredRating !== null && (
            <View
              style={{
                position: "absolute",
                bottom: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                marginBottom: "8px",
                backgroundColor: "#1f2937",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                zIndex: 1000,
                pointerEvents: "none",
              }}
            >
              {getLabel(hoveredRating)}
            </View>
          )}
        </View>
      </View>
    );
  }

  return null;
}
